import React, { createRef } from "react";
import withStyles from "@mui/styles/withStyles";
import {
  Button,
  Box,
  Tooltip,
  Fab,
  IconButton,
  Divider,
  Typography
} from "@mui/material";
import {
  RotateRight,
  Remove,
  Add,
  ChevronLeft,
  ChevronRight
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { withTranslation } from "react-i18next";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const initialScaleValue = 1.5;

const styles = theme => ({
  root: {
    textAlign: "center",
    width: "100%"
  },
  toolbar: {
    marginTop: theme.spacing(-2),
    "& > *": {
      paddingTop: theme.spacing(2)
    }
  },
  button: {
    margin: "0 10px"
  },
  page: {
    boxShadow: "0 0 10px rgba(0,0,0,0.5)",
    margin: "30px 0 20px 0"
  },
  pageDivider: {
    margin: "10px 0px"
  }
});

const getPdfViewerWidth = containerWidth =>
  (Math.min(containerWidth, 1000) - 16) / initialScaleValue;

class PdfViewer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      numPages: null,
      pageNumber: 0,
      link: props.link,
      rotate: 0,
      multiplePages: false,
      scale: initialScaleValue,
      isLoaded: false
    };
    this.pdfViewerRef = createRef();
  }

  onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ numPages });
    this.setState({ isLoaded: true });
  };

  onRotateClick = () => {
    let rotate = this.state.rotate + 90;
    this.setState({ rotate: rotate === 360 ? 0 : rotate });
  };

  goToPrevPage = () =>
    this.setState(state => ({ pageNumber: state.pageNumber - 1 }));

  goToNextPage = () =>
    this.setState(state => ({ pageNumber: state.pageNumber + 1 }));

  changeMultiplePagesMode = () =>
    this.setState(state => ({ multiplePages: !state.multiplePages }));

  onScaleUp = () => this.setState(state => ({ scale: state.scale + 0.1 }));

  onScaleDown = () => this.setState(state => ({ scale: state.scale - 0.1 }));

  renderPagination() {
    const { classes } = this.props;
    const { pageNumber, numPages } = this.state;

    const hasMultiplePages = numPages > 1;

    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        {hasMultiplePages && (
          <IconButton
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={this.goToPrevPage}
            disabled={pageNumber === 0}
            size="large"
          >
            <ChevronLeft />
          </IconButton>
        )}
        <Typography>
          Page {pageNumber + 1} of {numPages}
        </Typography>
        {hasMultiplePages && (
          <IconButton
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={this.goToNextPage}
            disabled={pageNumber + 1 === numPages}
            size="large"
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    );
  }

  renderContent() {
    const { pageNumber, numPages, multiplePages, scale } = this.state;
    const { classes } = this.props;

    const pdfViewerWidth = getPdfViewerWidth(
      this.pdfViewerRef.current?.getBoundingClientRect().width
    );

    const PdfPage = pageNumber => (
      <Page
        className={classes.page}
        scale={scale}
        pageNumber={pageNumber}
        width={pdfViewerWidth}
      />
    );

    return multiplePages
      ? Array.from(new Array(numPages), (el, index) => (
          <div key={`page_${index + 1}`}>
            {PdfPage(index + 1)}
            <Typography>
              {index + 1} / {numPages}
            </Typography>
            {index + 1 !== numPages && (
              <Divider className={classes.pageDivider} />
            )}
          </div>
        ))
      : PdfPage(pageNumber + 1);
  }

  render() {
    const {
      multiplePages: multiplePageView,
      scale,
      isLoaded,
      numPages
    } = this.state;
    const { classes, t } = this.props;

    const hasMultiplePages = numPages > 1;

    return (
      <div className={classes.root} ref={this.pdfViewerRef}>
        {isLoaded && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            className={classes.toolbar}
          >
            <Box display="flex" alignItems="center">
              <Fab
                size="small"
                className={classes.button}
                color="primary"
                onClick={this.onScaleDown}
                disabled={scale <= 1.0}
              >
                <Remove fontSize="small" />
              </Fab>
              <Typography>Scale: {scale.toFixed(1)}</Typography>
              <Fab
                size="small"
                className={classes.button}
                color="primary"
                onClick={this.onScaleUp}
                disabled={scale >= 3.0}
              >
                <Add fontSize="small" />
              </Fab>
            </Box>
            <Box>
              {hasMultiplePages && (
                <Button
                  className={classes.button}
                  color="primary"
                  variant="contained"
                  onClick={this.changeMultiplePagesMode}
                >
                  {multiplePageView ? "Single Page" : "Multiple Pages"}
                </Button>
              )}
            </Box>
            <Box>
              <Tooltip title={t("pdfViewer.rotateButtonTooltip")}>
                <IconButton
                  className={classes.button}
                  onClick={this.onRotateClick}
                  color="primary"
                  size="large"
                >
                  <RotateRight />
                </IconButton>
              </Tooltip>
            </Box>
            {hasMultiplePages &&
              !multiplePageView &&
              isLoaded &&
              this.renderPagination()}
          </Box>
        )}
        <Box px={1} overflow="scroll">
          <Box display="inline-block">
            <Document
              file={this.state.link}
              onLoadSuccess={this.onDocumentLoadSuccess}
              rotate={this.state.rotate}
            >
              {this.renderContent()}
            </Document>
          </Box>
        </Box>
        {!multiplePageView && isLoaded && this.renderPagination()}
      </div>
    );
  }
}

export default withTranslation()(withStyles(styles)(PdfViewer));
